import React, { useState } from "react";
import {
  Container,
  Box,
  Avatar,
  TextField,
  Button,
  Typography,
  Tabs,
  Tab,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockReset,
  Close,
  ArrowBack,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const AuthPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmNewPass, setShowConfirmNewPass] = useState(false);
  const [forgotData, setForgotData] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [forgotErrors, setForgotErrors] = useState({});

  const navigate = useNavigate();
  const initialRoute = typeof window !== 'undefined' ? sessionStorage.getItem('initialRoute') || '/' : '/';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotChange = (e) => {
    const { name, value } = e.target;
    setForgotData({ ...forgotData, [name]: value });

    setForgotErrors((prev) => {
      const next = { ...prev };
      if (name === "email") {
        if (!value) next.email = "Email required";
        else if (!value.includes("@")) next.email = "Enter a valid email";
        else delete next.email;
      }
      if (name === "newPassword") {
        if (!value) next.newPassword = "Password required";
        else if (value.length < 6) next.newPassword = "At least 6 characters";
        else delete next.newPassword;
        if (forgotData.confirmPassword && value !== forgotData.confirmPassword)
          next.confirmPassword = "Passwords do not match";
        else if (forgotData.confirmPassword) delete next.confirmPassword;
      }
      if (name === "confirmPassword") {
        if (!value) next.confirmPassword = "Confirm your password";
        else if (value !== forgotData.newPassword) next.confirmPassword = "Passwords do not match";
        else delete next.confirmPassword;
      }
      return next;
    });
  };

  const showAlert = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async () => {
    if (tab === 1 && formData.password !== formData.confirmPassword) {
      return showAlert("Passwords do not match", "error");
    }

    if (tab === 1 && formData.password.length < 6) {
      return showAlert("Password must be at least 6 characters", "error");
    }

    if (tab === 1 && !formData.email.includes("@")) {
      return showAlert("Please enter a valid email address", "error");
    }

    if (tab === 0 && !formData.name) {
      return showAlert("Please enter your name", "error");
    }

    setLoading(true);

    try {
      const endpoint = tab === 0
        ? "https://resume-maker-lc.onrender.com/api/auth/login"
        : "https://resume-maker-lc.onrender.com/api/auth/signup";

      const body = tab === 0
        ? { name: formData.name, password: formData.password }
        : formData;

      const res = await axios.post(endpoint, body);

      showAlert(res.data.message, "success");

      if (tab === 0) {
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("userId", res.data.user.id);
        setTimeout(() => navigate("/"), 1000);
      } else {
        setTimeout(() => {
          setTab(0);
          setFormData({ name: "", email: "", password: "", confirmPassword: "" });
          showAlert("Account created! Please login", "success");
        }, 1500);
      }
    } catch (err) {
      showAlert(err.response?.data?.message || "Error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async () => {
    const { email, newPassword, confirmPassword } = forgotData;

    if (!email) return showAlert("Please enter your registered email");
    if (!email.includes("@")) return showAlert("Please enter a valid email");
    if (!newPassword) return showAlert("Please enter a new password");
    if (newPassword.length < 6) return showAlert("Password must be at least 6 characters");
    if (newPassword !== confirmPassword) return showAlert("Passwords do not match");

    setForgotLoading(true);
    try {
      const res = await axios.post(
        "https://resume-maker-lc.onrender.com/api/auth/reset-password",
        { email, newPassword, confirmPassword }
      );

      showAlert(res.data.message, "success");
      setForgotData({ email: "", newPassword: "", confirmPassword: "" });
      setShowNewPass(false);
      setShowConfirmNewPass(false);
      setForgotOpen(false);
    } catch (err) {
      showAlert(err.response?.data?.message || "Reset failed. Try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleTabChange = (e, newValue) => {
    setTab(newValue);
    setFormData({ name: "", email: "", password: "", confirmPassword: "" });
    setShowPassword(false);
    setShowConfirm(false);
  };

  const fieldSx = {
    width: "100%",
    "& .MuiInputLabel-root": { color: "#0D4C8B" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
    "& .MuiOutlinedInput-root": {
      width: "100%",
      "& fieldset": { borderColor: "#B3D9F7" },
      "&:hover fieldset": { borderColor: "#0288D1" },
      "&.Mui-focused fieldset": { borderColor: "#002B5B", borderWidth: "2px" },
    },
  };

  // Submit button styles with dark blue theme
  const submitBtnSx = {
    mt: { xs: 3, sm: 4 },
    py: { xs: 1.2, sm: 1.5 },
    fontWeight: 600,
    fontSize: { xs: "14px", sm: "15px" },
    borderRadius: 2,
    background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
    color: "#fff",
    textTransform: "none",
    transition: "all 0.3s",
    width: "100%",
    "&:hover": {
      background: "linear-gradient(135deg, #001a36 0%, #026aa1 100%)",
      transform: "translateY(-1px)",
      boxShadow: "0 10px 20px rgba(0,43,91,0.25)",
    },
    "&.Mui-disabled": {
      background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
      color: "#fff",
      opacity: 0.7,
    },
  };

  const resetBtnSx = {
    py: { xs: 1.2, sm: 1.5 },
    fontWeight: 600,
    fontSize: { xs: "14px", sm: "15px" },
    borderRadius: 2,
    background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
    color: "#fff",
    textTransform: "none",
    width: "100%",
    "&:hover": {
      background: "linear-gradient(135deg, #001a36 0%, #026aa1 100%)",
      transform: "translateY(-1px)",
      boxShadow: "0 10px 20px rgba(0,43,91,0.25)",
    },
    "&.Mui-disabled": {
      background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
      color: "#fff",
      opacity: 0.7,
    },
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #002B5B 0%, #0288D1 50%, #0a4a7a 100%)",
          p: { xs: 1, sm: 2 },
          m: 0,
          position: "relative",
        }}
      >
        <IconButton
          onClick={() => navigate(initialRoute)}
          sx={{
            position: "absolute",
            top: { xs: 12, sm: 20 },
            left: { xs: 12, sm: 20 },
            zIndex: 10,
            bgcolor: "rgba(179, 217, 247, 0.15)",
            color: "#B3D9F7",
            "&:hover": {
              bgcolor: "rgba(179, 217, 247, 0.3)",
              transform: "scale(1.05)",
            },
            transition: "all 0.3s",
          }}
        >
          <ArrowBack />
        </IconButton>

        {!isMobile && (
          <>
            <Box
              sx={{
                position: "absolute",
                top: "10%",
                right: "5%",
                width: 300,
                height: 300,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(179,217,247,0.1) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "10%",
                left: "5%",
                width: 250,
                height: 250,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(2,136,209,0.08) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
          </>
        )}

        <Container
          maxWidth="sm"
          disableGutters={true}
          sx={{
            width: "100%",
            m: 0,
            p: { xs: 0, sm: 0 },
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={24}
            sx={{
              p: { xs: 2, sm: 4, md: 5 },
              borderRadius: { xs: 3, sm: 4 },
              textAlign: "center",
              background: "#ffffff",
              width: "100%",
              maxWidth: { xs: "100%", sm: "500px", md: "600px" },
              boxShadow: "0px 25px 50px -12px rgba(0,43,91,0.35)",
              position: "relative",
              zIndex: 1,
              mx: { xs: 1, sm: "auto" },
              my: { xs: 1, sm: 2 },
              overflow: "hidden",
            }}
          >
            <Box sx={{ mb: { xs: 1.5, sm: 2 }, width: "100%" }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 800,
                  letterSpacing: "1px",
                  background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Life Changers Ind
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#0288D1",
                  fontWeight: 500,
                  letterSpacing: "2px",
                  fontSize: { xs: "0.6rem", sm: "0.75rem" },
                }}
              >
                RESUME BUILDER
              </Typography>
            </Box>

            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                fontWeight: 700,
                mb: 1,
                color: "#002B5B",
                width: "100%",
              }}
            >
              {tab === 0 ? "Welcome Back" : "Get Started"}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#0D4C8B",
                mb: { xs: 2, sm: 3 },
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                width: "100%",
              }}
            >
              {tab === 0
                ? "Sign in to access your resumes and continue building"
                : "Create your account and build professional resumes in minutes"}
            </Typography>

            <Tabs
              value={tab}
              onChange={handleTabChange}
              centered
              sx={{
                mb: { xs: 2, sm: 3 },
                width: "100%",
                "& .MuiTabs-flexContainer": { justifyContent: "center" },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: { xs: "13px", sm: "15px" },
                  textTransform: "none",
                  color: "#0D4C8B",
                  transition: "all 0.3s",
                  minWidth: { xs: 80, sm: 120 },
                  p: { xs: "8px 12px", sm: "12px 16px" },
                },
                "& .Mui-selected": { color: "#002B5B !important", fontWeight: 700 },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#0288D1",
                  height: "3px",
                  borderRadius: "3px",
                },
              }}
            >
              <Tab label="Login" />
              <Tab label="Sign Up" />
            </Tabs>

            <Box sx={{ width: "100%" }}>
              <TextField
                fullWidth
                margin="normal"
                label="Full Name"
                name="name"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                variant="outlined"
                sx={{ ...fieldSx, width: "100%" }}
                size={isMobile ? "small" : "medium"}
              />

              {tab === 1 && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Email Address"
                  name="email"
                  type="email"
                  placeholder="hello@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ ...fieldSx, width: "100%" }}
                  size={isMobile ? "small" : "medium"}
                />
              )}

              <TextField
                fullWidth
                margin="normal"
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                variant="outlined"
                sx={{ ...fieldSx, width: "100%" }}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ color: "#0D4C8B", p: isMobile ? 0.5 : 1 }}
                        size={isMobile ? "small" : "medium"}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {tab === 1 && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Confirm Password"
                  type={showConfirm ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ ...fieldSx, width: "100%" }}
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirm(!showConfirm)}
                          sx={{ color: "#0D4C8B", p: isMobile ? 0.5 : 1 }}
                          size={isMobile ? "small" : "medium"}
                        >
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <Button
                fullWidth
                variant="contained"
                disabled={loading}
                sx={submitBtnSx}
                onClick={handleSubmit}
              >
                {loading ? "Please wait..." : (tab === 0 ? "Sign In" : "Create Account")}
              </Button>

              {tab === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    color: "#0288D1",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: { xs: "0.7rem", sm: "0.875rem" },
                    "&:hover": { textDecoration: "underline" },
                    width: "100%",
                    textAlign: "center",
                  }}
                  onClick={() => {
                    setForgotData({ email: "", newPassword: "", confirmPassword: "" });
                    setForgotOpen(true);
                  }}
                >
                  Forgot password?
                </Typography>
              )}

              <Divider sx={{ my: { xs: 2, sm: 3 }, width: "100%" }}>
                <Typography variant="caption" sx={{ color: "#0D4C8B", fontSize: { xs: "0.6rem", sm: "0.75rem" } }}>
                  Secure Authentication
                </Typography>
              </Divider>

              <Typography variant="body2" sx={{ color: "#0D4C8B", fontSize: { xs: "0.7rem", sm: "0.75rem" }, width: "100%", textAlign: "center" }}>
                {tab === 0 ? "Don't have an account? " : "Already have an account? "}
                <span
                  style={{ color: "#0288D1", fontWeight: 600, cursor: "pointer" }}
                  onClick={() => handleTabChange(null, tab === 0 ? 1 : 0)}
                >
                  {tab === 0 ? "Sign up " : "Log in"}
                </span>
              </Typography>

              {tab === 1 && (
                <Typography variant="caption" sx={{ mt: 2, color: "#0D4C8B", display: "block", fontSize: { xs: "0.6rem", sm: "0.7rem" }, width: "100%", textAlign: "center" }}>
                  By signing up, you agree to our{" "}
                  <span style={{ color: "#0288D1", cursor: "pointer" }}>Terms</span> and{" "}
                  <span style={{ color: "#0288D1", cursor: "pointer" }}>Privacy Policy</span>
                </Typography>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>

      <Dialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 4, sm: 6 },
            p: { xs: 1, sm: 2 },
            bgcolor: "background.paper",
            boxShadow: "0px 20px 40px rgba(0,43,91,0.2)",
            m: { xs: 1, sm: 0 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 0, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
            <Avatar sx={{ bgcolor: "#0288D1", width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 } }}>
              <LockReset sx={{ color: "#fff", fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
            </Avatar>
            <Box sx={{ textAlign: "left" }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 800, color: "#002B5B" }}>
                Reset Password
              </Typography>
              <Typography variant="caption" sx={{ color: "#0D4C8B", fontSize: { xs: "0.6rem", sm: "0.75rem" } }}>
                Provide your registered email and a new secure password
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setForgotOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: "#0D4C8B" }}
            size={isMobile ? "small" : "medium"}
          >
            <Close fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, px: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            margin="normal"
            label="Registered Email"
            name="email"
            type="email"
            placeholder="hello@example.com"
            value={forgotData.email}
            onChange={handleForgotChange}
            variant="outlined"
            sx={{ ...fieldSx, width: "100%" }}
            error={!!forgotErrors.email}
            helperText={forgotErrors.email}
            size={isMobile ? "small" : "medium"}
          />

          <TextField
            fullWidth
            margin="normal"
            label="New Password"
            type={showNewPass ? "text" : "password"}
            name="newPassword"
            placeholder="••••••••"
            value={forgotData.newPassword}
            onChange={handleForgotChange}
            variant="outlined"
            sx={{ ...fieldSx, width: "100%" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPass(!showNewPass)}
                    sx={{ color: "#0D4C8B" }}
                    size={isMobile ? "small" : "medium"}
                  >
                    {showNewPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={!!forgotErrors.newPassword}
            helperText={forgotErrors.newPassword}
            size={isMobile ? "small" : "medium"}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Confirm New Password"
            type={showConfirmNewPass ? "text" : "password"}
            name="confirmPassword"
            placeholder="••••••••"
            value={forgotData.confirmPassword}
            onChange={handleForgotChange}
            variant="outlined"
            sx={{ ...fieldSx, width: "100%" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmNewPass(!showConfirmNewPass)}
                    sx={{ color: "#0D4C8B" }}
                    size={isMobile ? "small" : "medium"}
                  >
                    {showConfirmNewPass ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={!!forgotErrors.confirmPassword}
            helperText={forgotErrors.confirmPassword}
            size={isMobile ? "small" : "medium"}
          />
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, flexDirection: "column", gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            disabled={forgotLoading}
            onClick={handleForgotSubmit}
            sx={resetBtnSx}
          >
            {forgotLoading ? "Resetting..." : "Reset Password"}
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={() => setForgotOpen(false)}
            sx={{
              color: "#0D4C8B",
              textTransform: "none",
              fontWeight: 500,
              fontSize: { xs: "13px", sm: "14px" },
              width: "100%",
              "&:hover": { color: "#002B5B" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: 16, sm: 24 } }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 2,
            fontWeight: 500,
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AuthPage;